import { cn, useSkeletonPulse } from '@hibro/ui-client';
import type { ReactNode } from 'react';

/**
 * ActivityBar loading placeholders (见 12). The outer box matches the final
 * custom rail slot so swapping in the real view does not jump the rail.
 *
 * Glyph items keep the 20px bone inside a 36px shell (`size-9 mx-[9px]`),
 * matching loaded icon rows. Faces (`showExpandedTitle: false`) are the same
 * 36px circle (`size-9 mx-[9px]`), matching the account avatar
 * (Figma 200:38562, bare 36). Editor / iframe / detach keep the shared
 * spinner; this is rail-only.
 */
export function RailItemSkeleton({
  expanded,
  showExpandedTitle,
  label,
  loadingLabel,
}: {
  expanded: boolean;
  /** Same flag the real custom trigger uses. False = self-explanatory face. */
  showExpandedTitle: boolean;
  label: string;
  loadingLabel: string;
}): ReactNode {
  const showTitle = expanded && showExpandedTitle;
  const face = !showExpandedTitle;

  return (
    <div
      role="status"
      aria-busy
      aria-label={loadingLabel}
      data-slot="rail-skeleton"
      className={cn(
        'flex items-center rounded-lg text-(--app-color-text-primary)',
        showTitle ? 'min-w-0 flex-1 gap-2.5' : 'min-w-0 flex-1',
        'size-9 mx-[9px]',
      )}
    >
      {face ? (
        <span
          aria-hidden
          data-slot="rail-skeleton-bone"
          className="size-9 shrink-0 animate-pulse rounded-full bg-current/20"
        />
      ) : (
        <span
          aria-hidden
          data-slot="rail-skeleton-icon"
          className="flex size-9 shrink-0 items-center justify-center"
        >
          <span
            data-slot="rail-skeleton-bone"
            className="size-5 animate-pulse rounded-md bg-current/20"
          />
        </span>
      )}
      {showTitle ? (
        <span className="min-w-0 truncate text-sm font-medium">{label}</span>
      ) : null}
    </div>
  );
}

/**
 * Dist-fetch overlay: keep the already-known icon and pulse it in place.
 * Faces fill the 36 slot; glyphs stay in the 20 box the unread badge anchors to.
 */
export function RailFetchingIcon({
  fetching,
  face = false,
  className,
  children,
}: {
  fetching: boolean;
  /**
   * A full-color resource icon fills the 36 slot itself (Figma `verified
   * server`). Glyphs stay in the 20 slot.
   */
  face?: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const pulse = useSkeletonPulse(fetching);
  return (
    <div
      aria-busy={fetching || undefined}
      className={cn(
        'relative flex shrink-0 items-center justify-center',
        face ? 'size-9' : 'size-5',
        pulse && 'animate-pulse',
        className,
      )}
    >
      {children}
    </div>
  );
}
